import React, { type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'

import { CheckIcon, RadioReceiverIcon, SmartphoneNfcIcon } from 'lucide-react'

import { alpha, Box, Stack, Typography, useTheme, type Theme } from '@mui/material'

// Type-only, so this erases at build time and cannot create a runtime cycle with the provider.
import type { ScanSource } from 'contexts'

/**
 * A SCAN-SOURCE ROW — "phone" or "desk reader", with its live connection status.
 *
 * WHY IT IS SHARED. This exact row now exists in three places: the Create Certificates scan block
 * (`ScanSessionBlock`), the pairing modal (`ScanConnectModal`) and the filter scan-source picker
 * (`ScanSourcePicker`). The first two had already drifted before this file existed — one leads
 * with a check-circle and no glyph, the other with a tinted glyph and no circle, and both
 * independently declare their own `SOURCE_ROW_HEIGHT = 42` because the constant could not be
 * shared. Per working-conventions §36 the second hand-roll becomes a shared component, so the
 * third one is written here instead of beside them.
 *
 * ⚠️ CONVERGENCE STILL OWED, BUT NARROWER NOW (VRG-322). `ScanSessionBlock` and `ScanConnectModal`
 * were both held by other sessions when this was extracted, so neither could be migrated in that
 * change. They remain the two hand-rolls. What changed since:
 *
 *  - `ReceivePackageModal` was rebuilt onto this component rather than becoming a FOURTH copy.
 *  - VRG-322's one open QUESTION — glyph or check-circle — is answered, as the `leading` prop
 *    rather than a rival treatment. `ScanSessionBlock`'s circle is `leading="status"` here, so it
 *    no longer needs a component of its own to keep it.
 *
 * ONE DECISION IS STILL OPEN, and it is small but real, so it is named rather than glossed:
 * `ScanSessionBlock`'s phone row renders "Show QR" BEFORE its status word, and this component only
 * has `trailing`, which is AFTER it. Migrating that row therefore either accepts the reorder
 * ("Not connected · Show QR") or adds a before-status slot. The reorder is probably right — the
 * argument in `trailing`'s own note, that the affordance belongs hard right, applies to a text
 * link as much as to a chevron — but it is a judgement call, not a mechanical swap, and pretending
 * otherwise is how the next person discovers it halfway through. `ScanConnectModal` has no such
 * problem: both its rows map onto this component's slots exactly as they stand.
 */

/**
 * Height of a scan-source row, pinned so a row does not move when its right-hand side changes —
 * "Not connected" becoming "Connected", or a row gaining a chevron.
 *
 * 42px is not arbitrary: it is what MUI's `<Radio>` imposed on these rows before the sources
 * became concurrent rather than a choice (9px padding around a 24px icon). Removing the radio
 * took the height with it and both rows collapsed to bare text height, at different heights from
 * each other. Duplicated verbatim in the two hand-rolls above; this is the copy to keep.
 */
export const SCAN_SOURCE_ROW_HEIGHT = 42

/**
 * Which physical thing delivers a scan. Canonical in `ScanRoutingContext` — it is a property of
 * the session, not of this row — so a new member forces both the routing branch and the glyph
 * below to answer for it.
 */
export type ScanSourceKind = ScanSource

const GLYPH: Record<ScanSourceKind, typeof SmartphoneNfcIcon> = {
  phone: SmartphoneNfcIcon,
  reader: RadioReceiverIcon,
}

const DEFAULT_LABEL: Record<ScanSourceKind, string> = {
  phone: 'Phone',
  reader: 'Desk reader',
}

/**
 * WHAT THE ROW LEADS WITH. The two readings VRG-322 found already diverged in the wild, now one
 * component with a prop rather than two components — which is what that task asks for ("If both
 * readings are wanted, add a prop rather than keeping two components").
 *
 * They answer different questions, and the question is set by whether the sources compete:
 *
 *  - `glyph` — WHICH source is this? Used where the operator is choosing one (`ScanSourcePicker`),
 *    so the row has to be identifiable at a glance before it is read.
 *  - `status` — is this one READY? Used where both sources are live at once and feed one session
 *    (`ScanSessionBlock`, `ReceivePackageModal`), so the useful fact is readiness, not identity.
 *    The identity is already carried by the label, which in those surfaces is a full sentence
 *    ("Scan with a hardware reader") rather than a bare noun.
 *
 * `glyph` stays the default: it is what the only pre-existing consumer of this component renders,
 * so the prop cannot change any current surface by being added.
 */
export type ScanSourceLeading = 'glyph' | 'status'

/**
 * Per-source readiness: a filled brand circle with a check when connected, a hollow outline when
 * not.
 *
 * A CHECK, NOT A RADIO, and the distinction is the whole reason this shape exists. A radio means
 * "pick one of these", which is false wherever this is used — the reader and the phone both feed
 * one session at the same time, so two rows can be ready at once and a radio would be promising
 * an exclusivity the session does not have. A check means "this one is ready", which is a status
 * and composes.
 */
const SourceStatusDot = ({ connected }: { connected: boolean }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: connected ? theme.palette.brand[500] : 'transparent',
        border: connected ? 'none' : `1.5px solid ${theme.palette.grey[400]}`,
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      }}
    >
      {connected && <CheckIcon size={13} strokeWidth={3} color={theme.palette.common.white} />}
    </Box>
  )
}

/**
 * THE SCAN-SOURCE SURFACE — one bordered container, styled per working-conventions §34's
 * SELECTABLE-CARD treatment. Exported so the two remaining hand-rolls (`ScanSessionBlock`,
 * `ScanConnectModal`) render the identical treatment while their CONVERGENCE onto this component
 * is still owed (VRG-322). A shared sx helper is not the convergence — it is the smallest thing
 * that makes the treatment itself impossible to diverge on in the meantime.
 *
 * ## Selectable card, NOT an action row — and the rule is explicit about the cost
 *
 * These rows carry a persistent per-source state (which source is chosen / connected, marked by
 * the row's own status indicator), which makes them selectable cards. §34:
 *
 *   > tint = "considering", border = "chosen" — never use a brand border for hover on a
 *   > selectable card (it collides with the selected state and destroys the distinction).
 *
 * So:
 *   rest            border `primary[100]`, paper
 *   hover           brand TINT only (0.06). The border does NOT move.
 *   selected        brand BORDER `brand[500]` + tint (0.1)
 *   selected+hover  keeps the full 0.1 tint rather than weakening to the hover tint
 *
 * This previously used the ACTION-ROW treatment (brand border + soft ring on hover), which is the
 * exact collision the rule names. Matches `FeaturesPage`'s activation tiles, the canonical
 * implementation.
 *
 * ## Focus is an OUTLINE, deliberately, because the border is no longer free
 *
 * The action-row treatment carried `:focus-visible` on the same brand border it used for hover.
 * Removing that border removes the ring with it, so keyboard focus needs its own channel — and it
 * must not read as `selected`, which is now what a brand BORDER means. A 2px `outline` is drawn
 * outside the border box: it cannot be mistaken for the 1px selected border, and it does not
 * disturb layout.
 *
 * ## ⚠️ `:has()`, NOT a bare `&:hover` / `&:focus-visible` — both halves are load-bearing
 *
 * The border lives on the outer card (it has to — the card wraps the row AND its collapsible
 * panel), while `role="button"` / `tabIndex` live on the inner row (they have to — putting them
 * on the card would nest the panel's own buttons inside a button role). So the interactive
 * element is never the styled element:
 *
 *  - `&:focus-visible` on the card cannot match. A keyboard user tabbing onto a row got NO ring
 *    at all while the mouse-hover ring worked perfectly. Verified in the browser — focused row,
 *    pointer moved away, computed `box-shadow: none`. Hover masks this completely, which is why
 *    it survived review by eye.
 *  - A bare `&:hover` matches the panel too, so with a TINT (unlike a border) hovering an
 *    expanded reader-setup panel would wash the whole card. Keying on the ROW's hover keeps the
 *    tint full-bleed inside the border while only the header row triggers it.
 *
 * `:focus-within` would also have matched the focus case but is wrong: it fires for anything
 * focusable INSIDE the card, so tabbing to "Shop readers" in the expanded panel would light the
 * row as though the row itself were about to activate.
 */
export const scanSourceSurfaceSx = (
  theme: Theme,
  { interactive = false, selected = false }: { interactive?: boolean; selected?: boolean } = {}
) => ({
  borderRadius: 2,
  border: `1px solid ${selected ? theme.palette.brand[500] : theme.palette.primary[100]}`,
  backgroundColor: selected ? alpha(theme.palette.brand[500], 0.1) : theme.palette.background.paper,
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
  ...(interactive
    ? {
        cursor: 'pointer',
        userSelect: 'none' as const,
        // Keyed on the ROW, not the card — see the `:has()` note above.
        '&:has(> [role="button"]:hover)': {
          backgroundColor: alpha(theme.palette.brand[500], selected ? 0.1 : 0.06),
        },
        '&:has(> [role="button"]:focus-visible)': {
          outline: `2px solid ${theme.palette.brand[600]}`,
          outlineOffset: 0,
        },
      }
    : null),
})

/**
 * Enter/Space on a row trigger, but ONLY when the row itself has focus.
 *
 * Without the `target !== currentTarget` guard a keypress on a control INSIDE the row activates
 * that control and then bubbles here, firing the row's action a second time. Learned in
 * `ScanSessionBlock`, where the "Show QR" link sits inside the row.
 */
const onRowKeyDown = (activate: () => void) => (e: ReactKeyboardEvent) => {
  if (e.target !== e.currentTarget) return
  if (e.key !== 'Enter' && e.key !== ' ') return
  e.preventDefault()
  activate()
}

export interface ScanSourceRowProps {
  source: ScanSourceKind
  /** Overrides the default noun. Use when the row has to say what the click DOES. */
  label?: string
  /** Live connection state. Drives the glyph tint and the default status word. */
  connected: boolean
  /**
   * The right-hand status word. Defaults to Connected / Not connected.
   *
   * Overridable because a phone is never "connected" before it has been paired, and telling an
   * operator "Not connected" about a phone they have not been asked to pair yet reads as a fault
   * rather than a next step.
   */
  status?: string
  /** Makes the row a trigger. Omit for a static status row. */
  onActivate?: () => void
  /**
   * The §34 "chosen" state — brand border + tint, held through hover.
   *
   * NO CALL SITE SETS THIS TODAY, and that is deliberate rather than an omission. `connected` is
   * a READINESS status, already carried by the status word and the row's own indicator, and both
   * sources feed one session concurrently — so tinting a connected row would assert a choice the
   * operator never made. The state exists here so that the day a surface genuinely does hold a
   * chosen source, it inherits the treatment instead of inventing a fourth one.
   */
  selected?: boolean
  /** Set when the row toggles a panel, so it announces the panel's state. */
  expanded?: boolean
  /** Glyph or readiness dot. See `ScanSourceLeading` — the choice follows the surface. */
  leading?: ScanSourceLeading
  /**
   * Rendered immediately after the label, INSIDE the row's own hit area (an `(i)` tooltip).
   *
   * Named for `FieldLabel`'s prop of the same job (§31) rather than inventing a second word for
   * "the thing that sits against a label".
   *
   * ⚠️ A control placed here must stop its own click from bubbling, or reaching for it toggles
   * the row it sits in — the explanation collapses the panel it explains. The row cannot do that
   * for the caller: it has no way to tell a control meant to be clickable from decoration.
   */
  adornment?: ReactNode
  /** Controls rendered after the status word (a link, a chevron). */
  trailing?: ReactNode
  /**
   * Quieter treatment for a source that cannot be picked right now.
   *
   * The row still renders — a source the operator does not have is a fact worth stating once,
   * with a way to fix it — but it must not compete with the source that works. Never lead with
   * an option the user cannot act on.
   */
  secondary?: boolean
  /** Rendered under the row, inside the same bordered container (setup guidance, a QR). */
  children?: ReactNode
}

export const ScanSourceRow = ({
  source,
  label,
  connected,
  status,
  onActivate,
  expanded,
  leading = 'glyph',
  adornment,
  trailing,
  secondary = false,
  selected = false,
  children,
}: ScanSourceRowProps) => {
  const theme = useTheme()
  const Glyph = GLYPH[source]
  const interactive = Boolean(onActivate)

  return (
    <Stack sx={{ px: 4, ...scanSourceSurfaceSx(theme, { interactive, selected }) }}>
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        {...(interactive
          ? {
              role: 'button',
              tabIndex: 0,
              'aria-expanded': expanded,
              onClick: onActivate,
              onKeyDown: onRowKeyDown(onActivate!),
            }
          : null)}
        sx={{ minHeight: SCAN_SOURCE_ROW_HEIGHT, outline: 'none' }}
      >
        {leading === 'status' ? (
          <SourceStatusDot connected={connected} />
        ) : (
          <Glyph
            strokeWidth={1}
            size={20}
            color={
              connected
                ? theme.palette.brand[500]
                : secondary
                  ? theme.palette.grey[400]
                  : theme.palette.grey[600]
            }
          />
        )}
        <Typography
          variant="body2"
          color={secondary ? 'text.secondary' : 'text.primary'}
          // The label absorbs the row's slack ONLY when it is the last thing on the left. With an
          // adornment the slack moves to that box instead (below), so the `(i)` stays against the
          // word it annotates. Leaving `flex: 1` here would push it the full width of the row and
          // park it beside the status word, where it reads as annotating THAT.
          sx={adornment ? undefined : { flex: 1 }}
        >
          {label ?? DEFAULT_LABEL[source]}
        </Typography>
        {adornment && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>{adornment}</Box>
        )}
        {/* Status word BEFORE the trailing slot, so an expand chevron stays hard right where
            every other accordion in the app puts it. Reversing them reads as "Desk reader ⌄ Not
            connected", with the affordance interrupting the row's own sentence. */}
        <Typography
          variant="body3"
          color={connected ? theme.palette.brand[500] : 'text.secondary'}
        >
          {status ?? (connected ? 'Connected' : 'Not connected')}
        </Typography>
        {trailing}
      </Stack>
      {children}
    </Stack>
  )
}

export default ScanSourceRow
